import React, { useState } from 'react';
import { MdArrowBack, MdBlock, MdChevronRight, MdFolder } from 'react-icons/md';
import { useId } from '@reach/auto-id';
import classNames from 'classnames';

import { Button } from '../button';
import { Icon } from '../icon';
import { IconContainer } from '../icon-container';

function getParentFolder(
  folders: Folder[],
  value: Folder,
  parentFolder: Folder | null = null
): Folder | null {
  if (folders.includes(value)) {
    return parentFolder;
  }

  for (const folder of folders) {
    if (!(folder.subFolders && folder.subFolders.length > 0)) {
      continue;
    }

    const match = getParentFolder(folder.subFolders, value, folder);

    if (match) {
      return match;
    }
  }

  return null;
}

export type Folder = {
  id: string;
  name: string;
  subFolders?: Folder[];
};

export type FolderSelectProps = {
  folders: Folder[];
  value: Folder | null;
  onChange: (value: Folder | null) => void;
};

export const FolderSelect = ({
  folders,
  value,
  onChange,
}: FolderSelectProps) => {
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(value);
  const selectId = useId();

  const parentFolder = currentFolder
    ? getParentFolder(folders, currentFolder)
    : null;
  const currentSubFolders = currentFolder
    ? currentFolder.subFolders ?? []
    : folders;

  const handleEnterFolder = (folder: Folder) => {
    setCurrentFolder(folder);
    onChange(folder);
  };

  const handleLeaveFolder = () => {
    setCurrentFolder(parentFolder);
    onChange(currentFolder);
  };

  const getItemId = (id: string) => `${selectId}-${id}`;

  return (
    <div className="bg-primary-50 border-t border-b border-gray-100">
      <div
        className={classNames(
          'flex space-x-2 items-center py-2 border-b border-gray-100',
          {
            'px-12': !currentFolder,
            'px-11': currentFolder,
          }
        )}
      >
        {currentFolder && (
          <Button
            variant="ghost"
            aria-label="Back"
            onClick={handleLeaveFolder}
            size="sm"
            onlyIcon
          >
            <Icon icon={<MdArrowBack />} />
          </Button>
        )}
        <div className="text-primary-500 font-medium truncate py-2">
          {currentFolder ? currentFolder.name : 'Main folders'}
        </div>
      </div>
      <ul
        role="menu"
        aria-activedescendant={value ? getItemId(value.id) : undefined}
        tabIndex={0}
        id={selectId}
        className="h-48 overflow-y-auto py-2"
      >
        {currentSubFolders.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center space-y-2">
            <IconContainer variant="light">
              <Icon icon={<MdBlock />} />
            </IconContainer>
            <div className="text-sm font-medium text-primary-500">
              No sub folders
            </div>
          </div>
        ) : (
          currentSubFolders.map((folder) => {
            const { id, name } = folder;

            return (
              <li
                key={id}
                id={getItemId(id)}
                role="menuitem"
                className="flex space-x-2 px-12 py-2 select-none hover:bg-primary-100"
                onDoubleClick={() => {
                  handleEnterFolder(folder);
                }}
              >
                <Icon
                  icon={<MdFolder className="flex-none text-primary-400" />}
                />
                <div className="flex-1 truncate">{name}</div>
                <button
                  type="button"
                  aria-label={`Open ${folder.name}`}
                  className="flex-none rounded text-primary-500 hover:bg-black hover:bg-opacity-10"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEnterFolder(folder);
                  }}
                >
                  <Icon icon={<MdChevronRight />} />
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};
